import React, { FC, useMemo } from "react";
import { Chip, IconToggle, Menu, Popup } from "@evergis/ui";
import { useSelector } from "react-redux";

import { FeatureCardPickLayerMenu, FeatureCardPickLayerMenuOption } from "./styled";

import { useToggle } from "../../../../hooks/useToggle";
import { useSelectFeaturesLayerName } from "../../../../hooks/selectFeatures/useSelectFeaturesLayerName";
import { useTableActiveLayers } from "../../../Table/hooks/useTableActiveLayers";
import { useDashboardLayers } from "../../../../hooks/project/useDashboardLayers";
import { useIsDashboard } from "../../../../hooks/location/useIsDashboard";
import { getProjectLayers } from "../../../../ducks/project/selectors";
import { getSelectedFeaturesTotalCounts } from "../../../../ducks/selectFeatures/selectors";

import { LayerPickMenuProps } from "./types";

export const LayerPickMenu: FC<LayerPickMenuProps> = ({ disabled }) => {
  const [layerMenuOpen, toggleLayerMenuOpen] = useToggle(false);
  const tableActiveLayers = useTableActiveLayers();
  const currentProjectLayers = useSelector(getProjectLayers);
  const [selectFeaturesLayerName, setSelectFeaturesLayerName] = useSelectFeaturesLayerName();
  const totalCounts = useSelector(getSelectedFeaturesTotalCounts);
  const dashboardLayersDynamicProps = useDashboardLayers();
  const isDashboard = useIsDashboard();

  const availableLayerOptions = useMemo(() => {
    return tableActiveLayers
      .filter(
        layer =>
          currentProjectLayers[layer.name]?.type === "ProxyService" ||
          (currentProjectLayers[layer.name]?.type !== "ProxyService" &&
            !!currentProjectLayers[layer.name]?.geometryAttribute),
      )
      .map(layer => {
        const isLayerVisible = isDashboard
          ? dashboardLayersDynamicProps?.layers?.[layer.name]?.isVisible
          : layer.isVisible;

        return {
          value: layer.name,
          text: (
            <FeatureCardPickLayerMenuOption isFaded={!isLayerVisible}>
              {layer.alias || layer.name}
            </FeatureCardPickLayerMenuOption>
          ),
          suffix: <Chip text={`${totalCounts?.[layer.name] ?? 0}`} />,
        };
      });
  }, [currentProjectLayers, dashboardLayersDynamicProps?.layers, isDashboard, tableActiveLayers, totalCounts]);

  return (
    <Popup
      zIndex={101}
      anchor={ref => (
        <IconToggle
          kind="layer_option"
          innerRef={ref}
          isSelected={layerMenuOpen}
          onClick={toggleLayerMenuOpen}
          disabled={disabled}
        />
      )}
      isVisible={layerMenuOpen}
      onRequestClose={toggleLayerMenuOpen}
    >
      <FeatureCardPickLayerMenu>
        <Menu
          options={availableLayerOptions}
          selectOption={option => {
            toggleLayerMenuOpen();
            setSelectFeaturesLayerName(option?.[0]?.value);
          }}
          selectedOptions={
            availableLayerOptions.find(item => item.value === selectFeaturesLayerName)
              ? [availableLayerOptions.find(item => item.value === selectFeaturesLayerName)]
              : []
          }
        />
      </FeatureCardPickLayerMenu>
    </Popup>
  );
};
